import { ref, watch, inject, onBeforeUnmount } from 'vue';

const oneAccordionMode = ref(window.miniCartCustomSettings?.oneAccordion || false);

export const ACCORDION_SCOPE_KEY = 'miniCartAccordionScope';
const defaultScope = Symbol('miniCartAccordionDefaultScope');
const activeAccordionByScope = new Map();
const accordionCloseCallbacksByScope = new Map();

const getActiveAccordion = (scope) => {
  let activeAccordion = activeAccordionByScope.get(scope);
  if (!activeAccordion) {
    activeAccordion = ref(null);
    activeAccordionByScope.set(scope, activeAccordion);
  }
  return activeAccordion;
};

const getCloseCallbacks = (scope) => {
  let callbacks = accordionCloseCallbacksByScope.get(scope);
  if (!callbacks) {
    callbacks = new Set();
    accordionCloseCallbacksByScope.set(scope, callbacks);
  }
  return callbacks;
};

export function createAccordionScope() {
  return Symbol('miniCartAccordionScope');
}

export function closeAllAccordions(scope) {
  if (scope === undefined) {
    accordionCloseCallbacksByScope.forEach((callbacks, scopeKey) => {
      const activeAccordion = getActiveAccordion(scopeKey);
      activeAccordion.value = null;
      callbacks.forEach((closeCallback) => {
        closeCallback();
      });
    });
    return;
  }

  const activeAccordion = getActiveAccordion(scope);
  activeAccordion.value = null;
  const callbacks = accordionCloseCallbacksByScope.get(scope);
  if (!callbacks) return;
  callbacks.forEach((closeCallback) => {
    closeCallback();
  });
}

if (typeof window !== 'undefined') {
  window.closeAllMiniCartAccordions = closeAllAccordions;
}

export function useAccordion(accordionId, scopeOverride) {
  const scope = scopeOverride ?? inject(ACCORDION_SCOPE_KEY, defaultScope);
  const activeAccordion = getActiveAccordion(scope);
  const isOpen = ref(false);

  const close = () => {
    if (oneAccordionMode.value && activeAccordion.value === accordionId) {
      activeAccordion.value = null;
    }
    isOpen.value = false;
  };

  const closeCallbacks = getCloseCallbacks(scope);
  closeCallbacks.add(close);

  onBeforeUnmount(() => {
    closeCallbacks.delete(close);
    if (closeCallbacks.size === 0) {
      accordionCloseCallbacksByScope.delete(scope);
      activeAccordionByScope.delete(scope);
    }
  });

  const toggle = () => {
    if (oneAccordionMode.value) {
      if (!isOpen.value) {
        activeAccordion.value = accordionId;
        isOpen.value = true;
      } else {
        activeAccordion.value = null;
        isOpen.value = false;
      }
    } else {
      isOpen.value = !isOpen.value;
    }
  };

  watch(activeAccordion, (newActive) => {
    if (oneAccordionMode.value) {
      if (newActive !== accordionId && newActive !== null) {
        isOpen.value = false;
      }
    }
  });

  const open = () => {
    if (oneAccordionMode.value) {
      activeAccordion.value = accordionId;
    }
    isOpen.value = true;
  };

  return {
    isOpen,
    toggle,
    open,
    close
  };
}
